import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Trophy, ArrowLeft, Loader2, Crown } from 'lucide-react';
import { api, apiError } from './api';

const PLANS = [
  { id: 'os_free', name: 'Tournevo OS', price: 'Free', sub: 'Get started', icon: Zap, accent: '#8E8E93', buyable: false,
    features: ['Up to 5 matches', 'Live match engine', 'Match timeline & events'] },
  { id: 'os_pro', name: 'Tournevo OS Pro', price: '£4.99', sub: 'per month', icon: Crown, accent: '#00BFFF', buyable: true, highlight: true,
    features: ['Unlimited matches', 'Performance analytics', 'Referee assessments', 'Everything in Free'] },
  { id: 'tournament_pro', name: 'Tournament Pro', price: '£19.99', sub: 'per tournament', icon: Trophy, accent: '#00D26A', buyable: true,
    features: ['1 division', 'Unlimited referees', 'Auto scheduler & brackets', 'Public live portal'] },
  { id: 'tournament_plus', name: 'Tournament Plus', price: '£49.99', sub: 'per tournament', icon: Trophy, accent: '#FFD700', buyable: true,
    features: ['Up to 5 divisions', 'Unlimited referees', 'AI finals builder', 'Stripe team entries'] },
  { id: 'tournament_elite', name: 'Tournament Elite', price: '£99.99', sub: 'per tournament', icon: Trophy, accent: '#FF6BD6', buyable: true,
    features: ['Unlimited divisions', 'Unlimited referees', 'Everything in Plus', 'Priority everything'] },
];

export default function Pricing() {
  const nav = useNavigate();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const loggedIn = !!localStorage.getItem('rc_token');

  const buy = async (product) => {
    setErr('');
    if (!loggedIn) { nav('/login?next=/pricing'); return; }
    setBusy(product);
    try {
      const { data } = await api.post('/billing/checkout', { product, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setErr(apiError(e)); setBusy(''); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans">
      <div className="max-w-6xl mx-auto px-5 py-8">
        <button onClick={() => nav(-1)} data-testid="pricing-back" className="text-gray-400 hover:text-white flex items-center gap-2 text-sm mb-8"><ArrowLeft size={16} /> Back</button>
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-black">Plans & Pricing</h1>
          <p className="text-gray-400 mt-3 text-base max-w-xl mx-auto">Run grassroots matches for free, go Pro for analytics, and pay per tournament only when you run one.</p>
        </div>
        {err && <p className="text-center text-[#FF3B30] mb-6" data-testid="pricing-error">{err}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {PLANS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`}
                className={`relative rounded-2xl p-5 flex flex-col border ${p.highlight ? 'border-[#00BFFF] bg-[#0d1820]' : 'border-gray-800 bg-[#141414]'}`}>
                {p.highlight && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#00BFFF] text-black text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider">Popular</span>}
                <Icon size={26} style={{ color: p.accent }} />
                <h3 className="text-lg font-black mt-3">{p.name}</h3>
                <div className="mt-2 mb-1"><span className="text-3xl font-black">{p.price}</span></div>
                <p className="text-xs text-gray-500 mb-4">{p.sub}</p>
                <ul className="space-y-2 flex-1 mb-5">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300"><Check size={15} className="mt-0.5 shrink-0" style={{ color: p.accent }} />{f}</li>
                  ))}
                </ul>
                {p.buyable ? (
                  <button data-testid={`buy-${p.id}`} onClick={() => buy(p.id)} disabled={busy === p.id}
                    className="w-full py-3 rounded-xl font-bold text-black flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: p.accent }}>
                    {busy === p.id ? <Loader2 size={16} className="animate-spin" /> : null}{loggedIn ? 'Get started' : 'Sign in to buy'}
                  </button>
                ) : (
                  <div className="w-full py-3 rounded-xl font-bold text-center bg-[#1C1C1E] text-gray-500 border border-gray-800">Included</div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-600 mt-8">Secure payments by Stripe · Tournament plans are a one-off charge per tournament · OS Pro renews monthly.</p>
      </div>
    </div>
  );
}
